import type { BookingResponse } from '@/shared/api';
import { groupBookings, type BookingListEntry } from '../components/bookingsGrouping';

export type AgendaSectionKind = 'today' | 'house' | 'day' | 'past' | 'flat';
export type AgendaBlockTone = 'arrival' | 'departure';

export interface AgendaBlock {
  tone: AgendaBlockTone | null;
  entries: BookingListEntry[];
}

export interface AgendaSection {
  key: string;
  kind: AgendaSectionKind;
  /** Anreisetag eines Tagesabschnitts (yyyy-MM-dd). */
  date: string | null;
  blocks: AgendaBlock[];
  /** Erster Abschnitt seiner Art bekommt eine Trennzeile darüber. */
  divider: 'upcoming' | 'past' | null;
}

type Place =
  | { kind: 'today'; tone: AgendaBlockTone }
  | { kind: 'house' }
  | { kind: 'day'; date: string }
  | { kind: 'past' };

/**
 * Für Phasen ohne Zeitpunkt (ausstehend, Daten prüfen, keine): Lage nach den Daten gegen das
 * Heute des Servers, sonst des Objekts – mit der tatsächlichen Abreise, wo es sie gibt.
 */
function placeByDates(row: BookingResponse, today: string): Place {
  const day = row.objectToday ?? today;
  const checkOut = row.actualDepartureDate ?? row.checkOutDate;
  if (row.checkInDate === day) return { kind: 'today', tone: 'arrival' };
  if (checkOut === day) return { kind: 'today', tone: 'departure' };
  if (checkOut < day) return { kind: 'past' };
  if (row.checkInDate < day) return { kind: 'house' };
  return { kind: 'day', date: row.checkInDate };
}

/**
 * Wohin eine Buchung gehört. Wo die Phase des Servers (Objektzeit) einen Zeitpunkt hat, gilt sie;
 * Stornos stehen am Anreisetag, solange er kommt, ausstehende und unklare Buchungen nach Datum.
 */
function placeOf(row: BookingResponse, today: string): Place {
  switch (row.stayPhase) {
    case 'Arrival':
      return { kind: 'today', tone: 'arrival' };
    case 'Departure':
      return { kind: 'today', tone: 'departure' };
    case 'InHouse':
      return { kind: 'house' };
    case 'Completed':
    case 'NoShow':
      return { kind: 'past' };
    case 'Cancelled':
      return row.checkInDate >= (row.objectToday ?? today)
        ? { kind: 'day', date: row.checkInDate }
        : { kind: 'past' };
    case 'Upcoming':
      return { kind: 'day', date: row.checkInDate };
    default:
      return placeByDates(row, today);
  }
}

const leadRow = (entry: BookingListEntry) =>
  entry.kind === 'single'
    ? entry.row
    : (entry.rows.find((row) => row.status !== 'Cancelled') ?? entry.rows[0]!);

/**
 * Die Liste als Tagesansicht: Heute (Anreisen, Abreisen), Im Haus, je Anreisetag, Vergangen.
 * Reine Darstellung der gelieferten Seite in Server-Reihenfolge; eine Mehrzimmer-Reservierung
 * bleibt zusammen und steht dort, wo ihr erstes nicht storniertes Zimmer hingehört.
 */
export function bookingAgenda(rows: BookingResponse[], today: string): AgendaSection[] {
  const todaySection: AgendaSection = {
    key: 'today',
    kind: 'today',
    date: today,
    blocks: [],
    divider: null,
  };
  const arrivals: BookingListEntry[] = [];
  const departures: BookingListEntry[] = [];
  const house: BookingListEntry[] = [];
  const past: BookingListEntry[] = [];
  const days = new Map<string, BookingListEntry[]>();
  for (const entry of groupBookings(rows)) {
    const place = placeOf(leadRow(entry), today);
    if (place.kind === 'today') (place.tone === 'arrival' ? arrivals : departures).push(entry);
    else if (place.kind === 'house') house.push(entry);
    else if (place.kind === 'past') past.push(entry);
    else days.set(place.date, [...(days.get(place.date) ?? []), entry]);
  }
  const sections: AgendaSection[] = [];
  if (arrivals.length) todaySection.blocks.push({ tone: 'arrival', entries: arrivals });
  if (departures.length) todaySection.blocks.push({ tone: 'departure', entries: departures });
  if (todaySection.blocks.length) sections.push(todaySection);
  if (house.length) {
    sections.push({
      key: 'house',
      kind: 'house',
      date: null,
      blocks: [{ tone: null, entries: house }],
      divider: null,
    });
  }
  [...days.entries()].forEach(([date, entries], index) =>
    sections.push({
      key: `day:${date}`,
      kind: 'day',
      date,
      blocks: [{ tone: null, entries }],
      divider: index === 0 ? 'upcoming' : null,
    }),
  );
  if (past.length) {
    sections.push({
      key: 'past',
      kind: 'past',
      date: null,
      blocks: [{ tone: null, entries: past }],
      divider: 'past',
    });
  }
  return sections;
}

/** Ohne Sortierung nach Anreise ergibt eine Einteilung nach Tagen keinen Sinn: eine Liste. */
export function flatAgenda(rows: BookingResponse[]): AgendaSection[] {
  const entries = groupBookings(rows);
  return entries.length
    ? [{ key: 'flat', kind: 'flat', date: null, blocks: [{ tone: null, entries }], divider: null }]
    : [];
}

export function entryCount(section: AgendaSection): number {
  return section.blocks.reduce((sum, block) => sum + block.entries.length, 0);
}
